/**
 * @file PickingTaskDialogs.tsx
 * @description Диспетчер диалогов экрана задания (phase19.14, экран B шага 0).
 *
 * Четыре модалки (ручное количество, недостача строки, проведение, исход
 * «недостача») — это один вопрос «что сейчас открыто», а не четыре куска
 * разметки посреди страницы. Реестр `kind → компонент` вместо if-каскада:
 * новая модалка = ветка здесь, страница не меняется.
 */

import { CompletePickDialog } from "./CompletePickDialog";
import { PickQuantityDialog } from "./PickQuantityDialog";
import { ShortageDialog } from "./ShortageDialog";
import { TaskShortageDialog } from "./TaskShortageDialog";
import type {
  PickingShortageReason,
  PickingTaskDetailDto,
} from "../../../lib/api-picking";

/** Что открыто на экране задания. */
export type PickingDialogState =
  | { kind: "none" }
  | { kind: "qty"; itemId: string }
  | { kind: "itemShortage"; itemId: string }
  | { kind: "complete" }
  | { kind: "taskShortage" };

interface Props {
  state: PickingDialogState;
  task: PickingTaskDetailDto;
  /** Строки с недостачей — список для диалога исхода. */
  shortageLines: string[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSetQty: (itemId: string, qty: number) => Promise<void>;
  onItemShortage: (
    itemId: string,
    input: { shortageQty: number; reason: PickingShortageReason; note?: string },
  ) => Promise<boolean>;
  onComplete: () => Promise<boolean>;
  onTaskShortage: (note: string) => Promise<boolean>;
}

export function PickingTaskDialogs({
  state,
  task,
  shortageLines,
  busy,
  error,
  onClose,
  onSetQty,
  onItemShortage,
  onComplete,
  onTaskShortage,
}: Props) {
  const item =
    state.kind === "qty" || state.kind === "itemShortage"
      ? (task.items.find((i) => i.id === state.itemId) ?? null)
      : null;

  if (state.kind === "qty" && item) {
    return (
      <PickQuantityDialog
        item={item}
        saving={busy}
        error={error}
        onSubmit={async (qty) => {
          await onSetQty(item.id, qty);
          onClose();
        }}
        onClose={onClose}
      />
    );
  }

  if (state.kind === "itemShortage" && item) {
    return (
      <ShortageDialog
        item={item}
        saving={busy}
        error={error}
        onSubmit={async (input) => {
          if (await onItemShortage(item.id, input)) onClose();
        }}
        onClose={onClose}
      />
    );
  }

  if (state.kind === "complete") {
    return (
      <CompletePickDialog
        taskNumber={task.number}
        orderNumber={task.orderNumber}
        itemsCount={task.itemsCount}
        saving={busy}
        error={error}
        onConfirm={async () => {
          if (await onComplete()) onClose();
        }}
        onClose={onClose}
      />
    );
  }

  if (state.kind === "taskShortage") {
    return (
      <TaskShortageDialog
        taskNumber={task.number}
        lines={shortageLines}
        saving={busy}
        error={error}
        onConfirm={async (note) => {
          if (await onTaskShortage(note)) onClose();
        }}
        onClose={onClose}
      />
    );
  }

  return null;
}
